import { MARKET_PATTERN, SLUG_PATTERN } from "@lucky-try/shared";
import { z } from "zod";
import {
  hexColorSchema,
  httpUrlSchema,
  httpsUrlSchema,
  isoDate,
  isoDateTime,
  nullableText,
  paginationQuerySchema,
  queryBoolean,
} from "./common";

export const gameStatusSchema = z.enum(["draft", "published", "archived"]);
export type GameStatus = z.infer<typeof gameStatusSchema>;

export const orientationSchema = z.enum(["landscape", "portrait"]);
export type Orientation = z.infer<typeof orientationSchema>;

export const gameProviderSchema = z.enum([
  "gamedistribution",
  "external",
  "gamepush",
  "self_hosted",
]);
export type GameProvider = z.infer<typeof gameProviderSchema>;

export const storePlatformSchema = z.enum(["app_store", "google_play", "crazy_games", "apk"]);
export type StorePlatform = z.infer<typeof storePlatformSchema>;

export const slugSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(SLUG_PATTERN, "Lowercase latin letters, digits and dashes");

export const marketSchema = z
  .string()
  .regex(MARKET_PATTERN, "Use `global` or a 2-letter country code");

export const iconNameSchema = z
  .string()
  .regex(/^ph:[a-z0-9-]+$/, "Phosphor icon name, e.g. ph:sword-duotone");

const variantBase = {
  id: z.number().int().positive().optional(),
  market: marketSchema,
  isEnabled: z.boolean().default(true),
};

const buildPathSegment = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[A-Za-z0-9._-]+$/, "Letters, digits, dot, dash or underscore");

export const gameVariantInputSchema = z.discriminatedUnion("provider", [
  z.object({
    ...variantBase,
    provider: z.literal("gamedistribution"),
    url: httpsUrlSchema,
    config: z
      .object({
        assetId: z
          .string()
          .regex(/^[a-f0-9]{32}$/)
          .optional(),
      })
      .default({}),
  }),
  z.object({
    ...variantBase,
    provider: z.literal("external"),
    url: httpsUrlSchema,
    config: z.object({}).default({}),
  }),
  z.object({
    ...variantBase,
    provider: z.literal("gamepush"),
    url: httpsUrlSchema,
    projectId: z.string().trim().min(1).max(64),
    config: z.object({ platformKey: z.string().min(1).max(64).optional() }).default({}),
  }),
  z.object({
    ...variantBase,
    provider: z.literal("self_hosted"),
    directory: buildPathSegment,
    version: buildPathSegment,
    config: z.object({}).default({}),
  }),
]);
export type GameVariantInput = z.input<typeof gameVariantInputSchema>;
export type GameVariant = z.output<typeof gameVariantInputSchema>;

export const storeLinkSchema = z.object({ platform: storePlatformSchema, url: httpUrlSchema });
export type StoreLink = z.infer<typeof storeLinkSchema>;

export const gameInputSchema = z
  .object({
    slug: slugSchema,
    name: z.string().trim().min(1).max(200),
    status: gameStatusSchema,
    categoryId: z.number().int().positive(),
    author: nullableText(200),
    releaseDate: isoDate.nullable(),
    briefDescription: nullableText(500),
    description: nullableText(20_000),
    seoTitle: nullableText(200),
    seoDescription: nullableText(320),
    orientation: orientationSchema,
    backgroundColor: hexColorSchema.nullable(),
    ratingValue: z.number().min(0).max(5).nullable(),
    ratingCount: z.number().int().min(0).nullable(),
    isPopular: z.boolean(),
    isNew: z.boolean(),
    isOriginal: z.boolean(),
    sortOrder: z.number().int().min(0).max(100_000),
    adsenseEligible: z.boolean(),
    disableAdblockNotice: z.boolean(),
    iconAssetId: z.uuid().nullable(),
    previewAssetId: z.uuid().nullable(),
    thumbnailAssetId: z.uuid().nullable(),
    storeLinks: z.array(storeLinkSchema).max(4),
    variants: z.array(gameVariantInputSchema).max(20),
  })
  .superRefine((game, ctx) => {
    const platforms = new Set<string>();
    game.storeLinks.forEach((link, index) => {
      if (platforms.has(link.platform)) {
        ctx.addIssue({
          code: "custom",
          path: ["storeLinks", index, "platform"],
          message: "Duplicate platform",
        });
      }
      platforms.add(link.platform);
    });
    const markets = new Set<string>();
    game.variants.forEach((variant, index) => {
      if (markets.has(variant.market)) {
        ctx.addIssue({
          code: "custom",
          path: ["variants", index, "market"],
          message: "Duplicate market",
        });
      }
      markets.add(variant.market);
    });
    if (game.status === "published" && !game.variants.some((v) => v.isEnabled !== false)) {
      ctx.addIssue({
        code: "custom",
        path: ["variants"],
        message: "A published game needs an enabled delivery variant",
      });
    }
    if (game.status === "published" && !game.iconAssetId) {
      ctx.addIssue({
        code: "custom",
        path: ["iconAssetId"],
        message: "A published game needs an icon",
      });
    }
  });
export type GameInput = z.input<typeof gameInputSchema>;

export const mediaAssetSchema = z.object({
  id: z.uuid(),
  storage: z.enum(["r2", "external"]),
  url: z.string(),
  contentType: z.string().nullable(),
  byteSize: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  alt: z.string().nullable(),
  originalFilename: z.string().nullable(),
  createdAt: isoDateTime,
});
export type MediaAsset = z.infer<typeof mediaAssetSchema>;

export const gameVariantSchema = z.object({
  id: z.number().int(),
  market: z.string(),
  provider: gameProviderSchema,
  url: z.string().nullable(),
  projectId: z.string().nullable(),
  directory: z.string().nullable(),
  version: z.string().nullable(),
  config: z.record(z.string(), z.unknown()),
  isEnabled: z.boolean(),
});
export type GameVariantRecord = z.infer<typeof gameVariantSchema>;

export const adminGameSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  status: gameStatusSchema,
  categoryId: z.number().int(),
  author: z.string().nullable(),
  releaseDate: z.string().nullable(),
  briefDescription: z.string().nullable(),
  description: z.string().nullable(),
  seoTitle: z.string().nullable(),
  seoDescription: z.string().nullable(),
  orientation: orientationSchema,
  backgroundColor: z.string().nullable(),
  ratingValue: z.number().nullable(),
  ratingCount: z.number().nullable(),
  isPopular: z.boolean(),
  isNew: z.boolean(),
  isOriginal: z.boolean(),
  sortOrder: z.number().int(),
  adsenseEligible: z.boolean(),
  editorialReviewedAt: isoDateTime.nullable(),
  disableAdblockNotice: z.boolean(),
  icon: mediaAssetSchema.nullable(),
  preview: mediaAssetSchema.nullable(),
  thumbnail: mediaAssetSchema.nullable(),
  storeLinks: z.array(storeLinkSchema),
  variants: z.array(gameVariantSchema),
  likeCount: z.number().int(),
  publishedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type AdminGame = z.infer<typeof adminGameSchema>;

export const adminGameListItemSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  status: gameStatusSchema,
  categoryId: z.number().int(),
  categoryName: z.string(),
  iconUrl: z.string().nullable(),
  isPopular: z.boolean(),
  isNew: z.boolean(),
  isOriginal: z.boolean(),
  adsenseEligible: z.boolean(),
  sortOrder: z.number().int(),
  markets: z.array(z.string()),
  providers: z.array(gameProviderSchema),
  updatedAt: isoDateTime,
});
export type AdminGameListItem = z.infer<typeof adminGameListItemSchema>;

export const adminGameListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  status: gameStatusSchema.optional(),
  categoryId: z.coerce.number().int().positive().optional(),
  provider: gameProviderSchema.optional(),
  popular: queryBoolean.optional(),
  isNew: queryBoolean.optional(),
  original: queryBoolean.optional(),
  adsense: queryBoolean.optional(),
  sort: z.enum(["name", "sortOrder", "updatedAt", "createdAt"]).default("updatedAt"),
  dir: z.enum(["asc", "desc"]).default("desc"),
});
export type AdminGameListQuery = z.infer<typeof adminGameListQuerySchema>;

export const gameBulkUpdateSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(500),
  patch: z
    .object({
      status: gameStatusSchema,
      categoryId: z.number().int().positive(),
      isPopular: z.boolean(),
      isNew: z.boolean(),
      isOriginal: z.boolean(),
      adsenseEligible: z.boolean(),
      sortOrder: z.number().int().min(0).max(100_000),
    })
    .partial()
    .refine((patch) => Object.keys(patch).length > 0, "Nothing to update"),
});
export type GameBulkUpdate = z.infer<typeof gameBulkUpdateSchema>;

export const categoryInputSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(100),
  // Intro text shown above the category grid; long enough for a few paragraphs.
  description: nullableText(4000),
  iconName: iconNameSchema,
  iconAssetId: z.uuid().nullable(),
  colorPrimary: hexColorSchema.nullable(),
  colorSecondary: hexColorSchema.nullable(),
  isActive: z.boolean(),
  isFeatured: z.boolean(),
  showInMenu: z.boolean(),
  sortOrder: z.number().int().min(0).max(100_000),
});
export type CategoryInput = z.input<typeof categoryInputSchema>;

export const adminCategorySchema = categoryInputSchema.extend({
  id: z.number().int(),
  gameCount: z.number().int(),
  publishedGameCount: z.number().int(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type AdminCategory = z.infer<typeof adminCategorySchema>;

export const categoryReorderSchema = z.object({
  order: z
    .array(z.object({ id: z.number().int().positive(), sortOrder: z.number().int().min(0) }))
    .min(1),
});

export const mediaListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  storage: z.enum(["r2", "external"]).optional(),
});

export const externalMediaInputSchema = z.object({
  url: httpsUrlSchema,
  alt: nullableText(300).optional(),
});

export const mediaUpdateSchema = z.object({ alt: nullableText(300) });

export const catalogReleaseSchema = z.object({
  id: z.number().int(),
  status: z.enum(["requested", "triggered", "failed", "skipped"]),
  note: z.string().nullable(),
  publishedGameCount: z.number().int(),
  requestedBy: z.string().nullable(),
  triggeredAt: isoDateTime.nullable(),
  error: z.string().nullable(),
  createdAt: isoDateTime,
});
export type CatalogRelease = z.infer<typeof catalogReleaseSchema>;

export const publishCatalogInputSchema = z.object({ note: nullableText(500).optional() });

export const catalogStatusSchema = z.object({
  lastRelease: catalogReleaseSchema.nullable(),
  publishedGameCount: z.number().int(),
  changedSinceLastRelease: z.number().int(),
  deployHookConfigured: z.boolean(),
});
export type CatalogStatus = z.infer<typeof catalogStatusSchema>;
